/**
 * Crop recipe: how the sheet shown in the georeference workspace was cut out
 * of the original preview, so the backend can rebuild the same cut-out from
 * the original PDF at a much higher resolution (see the R-Point backend's
 * modules/v1/shared/utils/cropRender.js).
 *
 *   base_size  [w, h] of the original preview image, unrotated
 *   rotation   0 | 90 | 180 | 270, clockwise, applied to the whole preview
 *   bbox       [x, y, w, h] of the cut-out, in the rotated preview's pixels
 *   polygons   masks, in the rotated preview's pixels; outside any is clear
 *
 * Every rotate and crop in the workspace works on the image currently shown,
 * which may itself already be rotated and cropped. These functions fold each
 * new operation into the recipe so it stays relative to the original preview,
 * exactly matching what the canvas code does to the pixels.
 */

/** The untouched preview. */
export function identityRecipe(width, height) {
  return { base_size: [width, height], rotation: 0, bbox: [0, 0, width, height], polygons: [] };
}

/** Size of the whole preview after the recipe's rotation. */
function rotatedBaseSize(recipe) {
  const [w, h] = recipe.base_size;
  return recipe.rotation % 180 === 0 ? [w, h] : [h, w];
}

/**
 * The recipe after the shown image is rotated by `delta` degrees clockwise
 * (any multiple of 90, negative allowed).
 *
 * Rotating the cut-out is the same as rotating the whole preview and moving
 * the box and masks with it: one clockwise quarter turn in a space H tall
 * sends (x, y) to (H - y, x).
 */
export function rotateRecipe(recipe, delta) {
  const turns = (((Math.round(delta / 90) % 4) + 4) % 4);
  let next = recipe;

  for (let i = 0; i < turns; i += 1) {
    const [, height] = rotatedBaseSize(next);
    const [x, y, w, h] = next.bbox;
    next = {
      ...next,
      rotation: (next.rotation + 90) % 360,
      bbox: [height - (y + h), x, h, w],
      polygons: next.polygons.map((polygon) => polygon.map(([px, py]) => [height - py, px])),
    };
  }

  return next;
}

/**
 * The recipe after the shown image is rotated by `rotation` and then cut to
 * `box` with `polygon` as its mask — both in the rotated shown image's pixels,
 * as the trace tool computes them.
 */
export function cropRecipeWith(recipe, rotation, polygon, box) {
  const turned = rotateRecipe(recipe, rotation);
  const [ox, oy] = turned.bbox;
  const [x, y, w, h] = box;

  return {
    ...turned,
    bbox: [ox + x, oy + y, w, h],
    polygons: [...turned.polygons, polygon.map(([px, py]) => [ox + px, oy + py])],
  };
}

/**
 * Draws the recipe's cut-out of `image` (the original preview, loaded) onto a
 * canvas: the same rotation, box and masks the workspace applied, used to show
 * a saved sheet as it was cropped, so its control points line up again.
 */
export function renderRecipe(image, recipe) {
  const [baseW, baseH] = recipe.base_size;
  const [rw, rh] = rotatedBaseSize(recipe);
  const [bx, by, bw, bh] = recipe.bbox;

  const rotated = document.createElement("canvas");
  rotated.width = rw;
  rotated.height = rh;
  const rc = rotated.getContext("2d");
  rc.imageSmoothingEnabled = true;
  rc.imageSmoothingQuality = "high";
  rc.save();
  if (recipe.rotation === 90) {
    rc.translate(rw, 0);
    rc.rotate(Math.PI / 2);
  } else if (recipe.rotation === 180) {
    rc.translate(rw, rh);
    rc.rotate(Math.PI);
  } else if (recipe.rotation === 270) {
    rc.translate(0, rh);
    rc.rotate((3 * Math.PI) / 2);
  }
  rc.drawImage(image, 0, 0, baseW, baseH);
  rc.restore();

  for (const polygon of recipe.polygons) {
    rc.globalCompositeOperation = "destination-in";
    rc.beginPath();
    polygon.forEach(([px, py], i) => (i === 0 ? rc.moveTo(px, py) : rc.lineTo(px, py)));
    rc.closePath();
    rc.fill();
  }

  const out = document.createElement("canvas");
  out.width = Math.max(1, Math.round(bw));
  out.height = Math.max(1, Math.round(bh));
  out.getContext("2d").drawImage(rotated, bx, by, bw, bh, 0, 0, bw, bh);
  return out;
}

/** Whether a stored recipe has the shape this screen can redraw. */
export function isUsableRecipe(recipe) {
  return Boolean(
    recipe &&
      Array.isArray(recipe.base_size) &&
      Array.isArray(recipe.bbox) &&
      recipe.bbox.length === 4 &&
      [0, 90, 180, 270].includes(recipe.rotation) &&
      Array.isArray(recipe.polygons)
  );
}

/*
 * Control points follow the image. Each rotate or crop of the shown image
 * applies the same change to the points instead of discarding them, so they
 * stay on the features they were placed on — which is what lets a sheet be
 * re-cropped, or started again from its original PDF, without placing every
 * point again.
 */

/** Points after the shown image (width x height) is turned `delta` degrees clockwise. */
export function rotatePoints(points, width, height, delta) {
  const turns = (((Math.round(delta / 90) % 4) + 4) % 4);
  let out = points;
  let h = height;
  let w = width;
  for (let i = 0; i < turns; i += 1) {
    const H = h;
    out = out.map((p) => ({ ...p, x: H - p.y, y: p.x }));
    [w, h] = [h, w];
  }
  return out;
}

export function insidePolygon(x, y, polygon) {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i, i += 1) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/**
 * Points after the shown image is cut to `box` [x, y, w, h] with `polygon` as
 * its mask (both in the shown image's pixels). Points outside are dropped.
 */
export function cropPoints(points, polygon, box) {
  const [bx, by, bw, bh] = box;
  return points
    .filter((p) => p.x >= bx && p.y >= by && p.x <= bx + bw && p.y <= by + bh)
    .filter((p) => !polygon || polygon.length < 3 || insidePolygon(p.x, p.y, polygon))
    .map((p) => ({ ...p, x: p.x - bx, y: p.y - by }));
}

/** Points on a recipe's cut-out, moved back onto the uncropped, unrotated preview. */
export function pointsToBase(points, recipe) {
  const [ox, oy] = recipe.bbox;
  const [rw, rh] = rotatedBaseSize(recipe);
  const onRotatedBase = points.map((p) => ({ ...p, x: p.x + ox, y: p.y + oy }));
  return rotatePoints(onRotatedBase, rw, rh, 360 - recipe.rotation);
}
